import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';

// Services
import { downloadService } from '../services/downloadService';
import { deviceDetectionService } from '../services/deviceDetectionService';

// Constants
import { DEVICE_DISPLAY_NAMES, DeviceType } from '../constants/device';

// Composables
import { useAsyncOperation } from './useAsyncOperation';

// Services
import { cloudinaryService } from '../services/cloudinaryService';

// Types
interface DeviceVariation {
  imageUrl?: string;
  dimensions?: {
    width: number;
    height: number;
  };
}

interface EnhancedDeviceVariation {
  id: string;
  imageUrl: string;
  thumbnailUrl?: string;
  dimensions: {
    width: number;
    height: number;
  };
  validationScore?: number;
  isPrimary: boolean;
}

interface Wallpaper {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  thumbnailUrl?: string;
  downloadCount: number;
  category?: string;
  categoryId?: string;
  tags: string[];
  isWallpaperPack: boolean;
  supportedDevices?: string[];
  deviceVariations?: Record<DeviceType, DeviceVariation>;
  deviceVariationsList?: Array<{
    device: DeviceType;
    variations: EnhancedDeviceVariation[];
  }>;
  // Legacy deviceCompatibility field removed - using new device system only
  // Live wallpaper fields
  isVideo?: boolean;
  videoUrl?: string;
  videoThumbnail?: string;
  videoDuration?: number;
  videoAspectRatio?: string;
  isAnimated?: boolean;
}

interface DeviceInfo {
  dimensions: { width: number; height: number };
  aspectRatio: string;
  variationCount?: number;
  hasMultipleVariations?: boolean;
  totalVariations?: number;
}

interface ResolutionOption {
  name: string;
  dimensions: string;
  size: string;
  url: string;
}

export function useWallpaperDetail() {
  // Initialize composables
  const { execute } = useAsyncOperation();
  const route = useRoute();
  const router = useRouter();

  // State
  const wallpaper = ref<Wallpaper | null>(null);
  const loading = ref(true);
  const error = ref<string | null>(null);
  const isDownloading = ref(false);
  const showImageViewer = ref(false);
  const selectedDevice = ref<DeviceType>(DeviceType.MOBILE_PHONE);
  const selectedVariationIndex = ref<Record<DeviceType, number>>({
    [DeviceType.MOBILE_PHONE]: 0,
    [DeviceType.IPHONE]: 0,
    [DeviceType.IPHONE_PRO]: 0,
    [DeviceType.ANDROID]: 0,
    [DeviceType.TABLET]: 0,
    [DeviceType.LAPTOP]: 0,
    [DeviceType.DESKTOP]: 0,
    [DeviceType.ULTRAWIDE]: 0,
    [DeviceType.TV]: 0,
    [DeviceType.WATCH]: 0
  });

  // Notification state
  const notification = ref({
    show: false,
    type: 'success',
    message: '',
    resolution: ''
  });

  // State for category resolution
  const resolvedCategoryName = ref<string>('');

  // Favorites state
  const isFavorited = ref(false);

  // Computed properties
  const categoryName = computed(() => {
    if (resolvedCategoryName.value) {
      return resolvedCategoryName.value;
    }

    if (!wallpaper.value) return '';

    if (wallpaper.value.category && wallpaper.value.category !== wallpaper.value.categoryId) {
      return wallpaper.value.category;
    }

    if (wallpaper.value.categoryId) {
      const categories: Record<string, string> = {
        'nature': 'Nature', 'abstract': 'Abstract', 'minimal': 'Minimal',
        'gradient': 'Gradient', 'dark': 'Dark', 'light': 'Light',
        'technology': 'Technology', 'animals': 'Animals', 'architecture': 'Architecture',
        'space': 'Space', 'minimalist': 'Minimalist', 'artistic': 'Artistic',
        'photography': 'Photography', 'gaming': 'Gaming'
      };
      return categories[wallpaper.value.categoryId] || wallpaper.value.categoryId;
    }

    return 'Uncategorized';
  });

  // Device management helper functions
  const getDeviceDisplayName = (deviceType: DeviceType): string => {
    return DEVICE_DISPLAY_NAMES[deviceType as DeviceType] || deviceType;
  };

  const getAvailableDevices = (wallpaper: Wallpaper): DeviceType[] => {
    if (!wallpaper) return [];

    if (wallpaper.isWallpaperPack) {
      if (wallpaper.deviceVariationsList && wallpaper.deviceVariationsList.length > 0) {
        return wallpaper.deviceVariationsList.map(deviceGroup => deviceGroup.device);
      }

      if (wallpaper.deviceVariations) {
        const variations = wallpaper.deviceVariations as Partial<Record<DeviceType, {
          imageUrl: string;
          dimensions?: { width: number; height: number };
          aspectRatio?: string;
        }>>;
        return Object.values(DeviceType).filter(deviceType =>
          variations[deviceType] !== undefined
        );
      }
    }

    const defaultDeviceOrder: DeviceType[] = [
      DeviceType.MOBILE_PHONE, DeviceType.TABLET, DeviceType.DESKTOP,
      DeviceType.ULTRAWIDE, DeviceType.LAPTOP, DeviceType.TV, DeviceType.WATCH
    ];
    return defaultDeviceOrder;
  };

  const availableDevices = computed(() => {
    if (!wallpaper.value) return [];
    return getAvailableDevices(wallpaper.value);
  });

  const deviceInfo = computed(() => {
    const info: Partial<Record<DeviceType, DeviceInfo>> = {};

    if (wallpaper.value?.isWallpaperPack) {
      if (wallpaper.value.deviceVariationsList && wallpaper.value.deviceVariationsList.length > 0) {
        wallpaper.value.deviceVariationsList.forEach(deviceGroup => {
          const deviceType = deviceGroup.device;
          const variations = deviceGroup.variations || [];
          const primaryVariation = variations.find(v => v.isPrimary) || variations[0];

          if (primaryVariation) {
            info[deviceType] = {
              dimensions: primaryVariation.dimensions,
              aspectRatio: calculateAspectRatio(primaryVariation.dimensions),
              variationCount: variations.length,
              hasMultipleVariations: variations.length > 1,
              totalVariations: variations.length
            };
          }
        });
      }
      else if (wallpaper.value.deviceVariations) {
        Object.entries(wallpaper.value.deviceVariations).forEach(([device, variation]) => {
          const deviceType = device as DeviceType;
          if (Object.values(DeviceType).includes(deviceType)) {
            const dimensions = variation.dimensions || { width: 1080, height: 1920 };
            info[deviceType] = {
              dimensions,
              aspectRatio: calculateAspectRatio(dimensions),
              variationCount: 1,
              hasMultipleVariations: false,
              totalVariations: 1
            };
          }
        });
      }
    }

    availableDevices.value.forEach(deviceType => {
      if (!info[deviceType]) {
        const defaultDimensions = getDeviceDimensions(wallpaper.value, deviceType);
        info[deviceType] = {
          dimensions: defaultDimensions,
          aspectRatio: calculateAspectRatio(defaultDimensions),
          variationCount: 1,
          hasMultipleVariations: false,
          totalVariations: 1
        };
      }
    });

    return info as Record<DeviceType, DeviceInfo>;
  });

  // Helper functions
  const calculateAspectRatio = (dimensions: { width: number; height: number }): string => {
    const gcd = (a: number, b: number): number => b === 0 ? a : gcd(b, a % b);
    const divisor = gcd(dimensions.width, dimensions.height);
    return `${dimensions.width / divisor}:${dimensions.height / divisor}`;
  };

  const mapToDevicePreviewType = (deviceType: DeviceType): string => {
    const deviceMap: Record<DeviceType, string> = {
      [DeviceType.MOBILE_PHONE]: 'phone', 
      [DeviceType.IPHONE]: 'iphone',
      [DeviceType.IPHONE_PRO]: 'iphone-pro', 
      [DeviceType.ANDROID]: 'android',
      [DeviceType.TABLET]: 'tablet', 
      [DeviceType.LAPTOP]: 'laptop',
      [DeviceType.DESKTOP]: 'desktop', 
      [DeviceType.ULTRAWIDE]: 'ultrawide',
      [DeviceType.TV]: 'tv', 
      [DeviceType.WATCH]: 'watch'
    };
    return deviceMap[deviceType] || 'desktop';
  };

  const getDeviceImageUrl = (wallpaper: Wallpaper, deviceType: DeviceType): string => {
    if (!wallpaper) return '';

    const isLiveWallpaper = !!(
      wallpaper.isVideo ||
      wallpaper.isAnimated ||
      (wallpaper.videoUrl && wallpaper.videoUrl.trim().length > 0)
    );

    if (isLiveWallpaper) {
      if (wallpaper.videoThumbnail && wallpaper.videoThumbnail.trim().length > 0) {
        return wallpaper.videoThumbnail;
      }

      if (wallpaper.thumbnailUrl && wallpaper.thumbnailUrl.trim().length > 0) {
        return wallpaper.thumbnailUrl;
      }

      if (wallpaper.videoUrl && wallpaper.videoUrl.trim().length > 0) {
        try {
          return cloudinaryService.generateThumbnailUrl(wallpaper.videoUrl, 1200, 800);
        } catch (error) {
          console.warn('[useWallpaperDetail] Failed to generate live wallpaper thumbnail from video URL:', error);
        }
      }

      if (wallpaper.imageUrl) {
        return wallpaper.imageUrl;
      }

      return '';
    }

    if (wallpaper.isWallpaperPack) {
      if (wallpaper.deviceVariationsList && wallpaper.deviceVariationsList.length > 0) {
        const deviceGroup = wallpaper.deviceVariationsList.find(group => group.device === deviceType);
        if (deviceGroup && deviceGroup.variations.length > 0) {
          const variationIndex = selectedVariationIndex.value[deviceType] || 0;
          const selectedVariation = deviceGroup.variations[variationIndex] || deviceGroup.variations[0];
          return selectedVariation.imageUrl;
        }
      }

      if (wallpaper.deviceVariations?.[deviceType]) {
        return wallpaper.deviceVariations[deviceType].imageUrl || wallpaper.imageUrl;
      }
    }

    return wallpaper.imageUrl;
  };

  const getDeviceDimensions = (wallpaper: Wallpaper | null, deviceType: DeviceType) => {
    if (!wallpaper) return { width: 1080, height: 1920 };

    if (wallpaper.isWallpaperPack) {
      if (wallpaper.deviceVariationsList && wallpaper.deviceVariationsList.length > 0) {
        const deviceGroup = wallpaper.deviceVariationsList.find(group => group.device === deviceType);
        if (deviceGroup && deviceGroup.variations.length > 0) {
          const variationIndex = selectedVariationIndex.value[deviceType] || 0;
          const selectedVariation = deviceGroup.variations[variationIndex] || deviceGroup.variations[0];
          return selectedVariation.dimensions;
        }
      }

      if (wallpaper.deviceVariations?.[deviceType]?.dimensions) {
        return wallpaper.deviceVariations[deviceType].dimensions || { width: 1920, height: 1080 };
      }
    }

    const defaultDimensions: Record<DeviceType, { width: number; height: number }> = {
      [DeviceType.MOBILE_PHONE]: { width: 1080, height: 1920 },
      [DeviceType.IPHONE]: { width: 1170, height: 2532 },
      [DeviceType.IPHONE_PRO]: { width: 1284, height: 2778 },
      [DeviceType.ANDROID]: { width: 1080, height: 2400 },
      [DeviceType.TABLET]: { width: 1536, height: 2048 },
      [DeviceType.LAPTOP]: { width: 1366, height: 768 },
      [DeviceType.DESKTOP]: { width: 1920, height: 1080 },
      [DeviceType.ULTRAWIDE]: { width: 3440, height: 1440 },
      [DeviceType.TV]: { width: 3840, height: 2160 },
      [DeviceType.WATCH]: { width: 396, height: 484 }
    };

    return defaultDimensions[deviceType] || { width: 1920, height: 1080 };
  };

  const currentImageUrl = computed(() => {
    if (!wallpaper.value) return '';
    return getDeviceImageUrl(wallpaper.value, selectedDevice.value);
  });

  const currentDimensions = computed(() => {
    if (!wallpaper.value) return { width: 1080, height: 1920 };
    return getDeviceDimensions(wallpaper.value, selectedDevice.value);
  });

  // Get all variations for a specific device
  const getDeviceVariations = (wallpaper: Wallpaper, deviceType: DeviceType): EnhancedDeviceVariation[] => {
    if (!wallpaper || !wallpaper.isWallpaperPack) return [];

    if (wallpaper.deviceVariationsList && wallpaper.deviceVariationsList.length > 0) {
      const deviceGroup = wallpaper.deviceVariationsList.find(group => group.device === deviceType);
      if (deviceGroup && deviceGroup.variations.length > 0) {
        return deviceGroup.variations;
      }
    }

    if (wallpaper.deviceVariations?.[deviceType]) {
      const legacyVariation = wallpaper.deviceVariations[deviceType];
      return [{
        id: `${deviceType}-legacy`,
        imageUrl: legacyVariation.imageUrl || wallpaper.imageUrl,
        thumbnailUrl: legacyVariation.imageUrl || wallpaper.imageUrl,
        dimensions: legacyVariation.dimensions || { width: 1920, height: 1080 },
        validationScore: 100,
        isPrimary: true
      }];
    }

    return [];
  };

  // Enhanced variation navigation
  const currentVariationIndex = computed(() => {
    return selectedVariationIndex.value[selectedDevice.value] || 0;
  });

  // Navigation methods
  const navigateToPreviousVariation = () => {
    if (!wallpaper.value || !selectedDevice.value) return;
    const variations = getDeviceVariations(wallpaper.value, selectedDevice.value);
    if (variations.length <= 1) return;

    const currentIndex = currentVariationIndex.value;
    const newIndex = currentIndex > 0 ? currentIndex - 1 : variations.length - 1;
    handleVariationSelection(selectedDevice.value, newIndex);
  };

  const navigateToNextVariation = () => {
    if (!wallpaper.value || !selectedDevice.value) return;
    const variations = getDeviceVariations(wallpaper.value, selectedDevice.value);
    if (variations.length <= 1) return;

    const currentIndex = currentVariationIndex.value;
    const newIndex = currentIndex < variations.length - 1 ? currentIndex + 1 : 0;
    handleVariationSelection(selectedDevice.value, newIndex);
  };

  // Handle variation selection for a specific device
  const handleVariationSelection = (deviceType: DeviceType, variationIndex: number) => {
    selectedVariationIndex.value[deviceType] = variationIndex;
  };

  // Touch/swipe support
  let touchStartX = 0;
  let touchStartY = 0;
  let isSwiping = false;

  const handleTouchStart = (event: TouchEvent) => {
    if (!wallpaper.value || !selectedDevice.value) return;
    const variations = getDeviceVariations(wallpaper.value, selectedDevice.value);
    if (variations.length <= 1) return;

    touchStartX = event.touches[0].clientX;
    touchStartY = event.touches[0].clientY;
    isSwiping = false;
  };

  const handleTouchMove = (event: TouchEvent) => {
    if (!wallpaper.value || !selectedDevice.value) return;
    const variations = getDeviceVariations(wallpaper.value, selectedDevice.value);
    if (variations.length <= 1) return;

    const touchCurrentX = event.touches[0].clientX;
    const touchCurrentY = event.touches[0].clientY;

    const deltaX = Math.abs(touchCurrentX - touchStartX);
    const deltaY = Math.abs(touchCurrentY - touchStartY);

    if (deltaX > deltaY && deltaX > 10) {
      isSwiping = true;
      if (deltaX > 30) {
        event.preventDefault();
      }
    }
  };

  const handleTouchEnd = (event: TouchEvent) => {
    if (!wallpaper.value || !selectedDevice.value || !isSwiping) return;
    const variations = getDeviceVariations(wallpaper.value, selectedDevice.value);
    if (variations.length <= 1) return;

    const touchEndX = event.changedTouches[0].clientX;
    const deltaX = touchEndX - touchStartX;
    const minSwipeDistance = 50;

    if (Math.abs(deltaX) > minSwipeDistance) {
      if (deltaX > 0) {
        navigateToPreviousVariation();
      } else {
        navigateToNextVariation();
      }
    }

    isSwiping = false;
  };

  // Utility function for file size estimation
  const estimateFileSize = (width: number, height: number): string => {
    const pixels = width * height;
    const estimatedBytes = pixels * 3;

    if (estimatedBytes > 1024 * 1024) {
      return `${(estimatedBytes / (1024 * 1024)).toFixed(1)} MB`;
    }
    return `${Math.round(estimatedBytes / 1024)} KB`;
  };

  // Methods
  const loadWallpaper = async () => {
    const result = await execute('loadWallpaper', async () => {
      const { wallpaperService } = await import('../firebase/services/wallpaperService');
      const wallpaperData = await wallpaperService.getWallpaperById(route.params.id as string);

      if (!wallpaperData) {
        throw new Error('Wallpaper not found');
      }

      return wallpaperData;
    }, {
      onSuccess: (wallpaperData) => {
        wallpaper.value = wallpaperData;
        initializeSelectedDevice();
        resolveCategoryName();
        checkIfFavorited();
      },
      onError: (err) => {
        error.value = err instanceof Error ? err.message : 'Failed to load wallpaper. Please try again later.';
      }
    });

    loading.value = !result;
  };

  const resolveCategoryName = async () => {
    if (!wallpaper.value) return;

    try {
      if (wallpaper.value.category && wallpaper.value.category !== wallpaper.value.categoryId) {
        resolvedCategoryName.value = wallpaper.value.category;
        return;
      }

      if (wallpaper.value.categoryId) {
        const { categoryService } = await import('../firebase/services/categoryService');
        try {
          const category = await categoryService.getCategory(wallpaper.value.categoryId);
          if (category && category.name) {
            resolvedCategoryName.value = category.name;
            return;
          }
        } catch (error) {
          console.warn('Could not fetch category from database:', error);
        }
      }
    } catch (error) {
      console.warn('Error resolving category name:', error);
    }
  };

  const initializeSelectedDevice = () => {
    if (!wallpaper.value) return;

    const detectedDevice = deviceDetectionService.detectDevice();
    const availableDevicesList = getAvailableDevices(wallpaper.value);

    if (wallpaper.value.isWallpaperPack && wallpaper.value.deviceVariations) {
      const bestDevice = deviceDetectionService.getBestAvailableDevice(wallpaper.value.deviceVariations);
      if (bestDevice && availableDevicesList.includes(bestDevice as DeviceType)) {
        selectedDevice.value = bestDevice as DeviceType;
        return;
      }
    }

    if (availableDevicesList.length > 0) {
      selectedDevice.value = availableDevicesList.includes(detectedDevice.type as DeviceType)
        ? detectedDevice.type as DeviceType
        : availableDevicesList[0];
    }
  };

  const handleDeviceSelection = (deviceType: DeviceType) => {
    if (!wallpaper.value) return;
    selectedDevice.value = deviceType;
  };

  const getAvailableResolutions = () => {
    if (!wallpaper.value) return [];

    if (wallpaper.value.isWallpaperPack && selectedDevice.value) {
      const deviceVariations = getDeviceVariations(wallpaper.value, selectedDevice.value);
      if (deviceVariations.length > 0) {
        const variationIndex = selectedVariationIndex.value[selectedDevice.value] || 0;
        const selectedVariation = deviceVariations[variationIndex] || deviceVariations[0];

        const resolutions = generateResolutionsFromDimensions(
          selectedVariation.dimensions.width,
          selectedVariation.dimensions.height,
          selectedVariation.imageUrl
        );

        return resolutions.map(res => ({
          ...res,
          name: res.name === 'Original' ? `${getDeviceDisplayName(selectedDevice.value)} Variation ${variationIndex + 1} (${res.dimensions})` : res.name
        }));
      }

      if (wallpaper.value.deviceVariations?.[selectedDevice.value]) {
        const variation = wallpaper.value.deviceVariations[selectedDevice.value];
        const dimensions = variation.dimensions || { width: 1920, height: 1080 };

        const resolutions = generateResolutionsFromDimensions(dimensions.width, dimensions.height, variation.imageUrl || wallpaper.value.imageUrl);

        return resolutions.map(res => ({
          ...res,
          name: res.name === 'Original' ? `${getDeviceDisplayName(selectedDevice.value)} (${res.dimensions})` : res.name
        }));
      }
    }

    return generateResolutionsFromDimensions(1920, 1080, wallpaper.value.imageUrl);
  };

  const generateResolutionsFromDimensions = (originalWidth: number, originalHeight: number, imageUrl: string) => {
    const resolutions = [];
    const aspectRatio = originalWidth / originalHeight;

    resolutions.push({
      name: 'Original',
      dimensions: `${originalWidth}×${originalHeight}`,
      size: estimateFileSize(originalWidth, originalHeight),
      url: imageUrl
    });

    const commonResolutions = [
      { width: 2560, height: 1440, name: '1440p' },
      { width: 1920, height: 1080, name: 'Full HD' },
      { width: 1366, height: 768, name: 'HD' },
      { width: 1024, height: 768, name: 'XGA' }
    ];

    for (const res of commonResolutions) {
      if (res.width <= originalWidth && res.height <= originalHeight) {
        let targetWidth: number, targetHeight: number;

        if (aspectRatio > res.width / res.height) {
          targetWidth = Math.min(res.width, originalWidth);
          targetHeight = Math.round(targetWidth / aspectRatio);
        } else {
          targetHeight = Math.min(res.height, originalHeight);
          targetWidth = Math.round(targetHeight * aspectRatio);
        }

        if (targetWidth < originalWidth * 0.95 || targetHeight < originalHeight * 0.95) {
          const dimensionStr = `${targetWidth}×${targetHeight}`;
          const alreadyExists = resolutions.some(r => r.dimensions === dimensionStr);

          if (!alreadyExists) {
            resolutions.push({
              name: res.name,
              dimensions: dimensionStr,
              size: estimateFileSize(targetWidth, targetHeight),
              url: imageUrl
            });
          }
        }
      }
    }

    return resolutions;
  };

  const downloadWallpaper = async (resolution: ResolutionOption) => {
    if (isDownloading.value || !wallpaper.value) return;

    try {
      isDownloading.value = true;

      const result = await downloadService.downloadWallpaper({
        wallpaperId: wallpaper.value.id,
        imageUrl: resolution.url,
        resolution: resolution.dimensions,
        deviceType: selectedDevice.value,
        isOptimal: true
      });

      if (result.success) {
        showDownloadSuccess(resolution);
        wallpaper.value.downloadCount++;
      } else {
        showDownloadError(new Error(result.error || 'Download failed'));
      }
    } catch (err) {
      console.error('Download failed:', err);
      const error = err instanceof Error ? err : new Error('Unknown error occurred');
      showDownloadError(error);
    } finally {
      isDownloading.value = false;
    }
  };

  const shareWallpaper = async () => {
    if (!wallpaper.value) return;

    try {
      const shareData = {
        title: wallpaper.value.title,
        text: `Check out this amazing wallpaper: ${wallpaper.value.title}`,
        url: window.location.href
      };

      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(window.location.href);
        notification.value = {
          show: true,
          type: 'success',
          message: 'Link copied to clipboard!',
          resolution: ''
        };
        setTimeout(hideNotification, 3000);
      }
    } catch (error) {
      console.error('Share failed:', error);
      // Fallback to clipboard if share fails
      try {
        await navigator.clipboard.writeText(window.location.href);
        notification.value = {
          show: true,
          type: 'success',
          message: 'Link copied to clipboard!',
          resolution: ''
        };
        setTimeout(hideNotification, 3000);
      } catch (clipboardError) {
        console.error('Clipboard fallback failed:', clipboardError);
        notification.value = {
          show: true,
          type: 'error',
          message: 'Unable to share or copy link',
          resolution: ''
        };
        setTimeout(hideNotification, 3000);
      }
    }
  };

  const toggleFavorite = async () => {
    if (!wallpaper.value) return;

    try {
      // Toggle the local state immediately for better UX
      isFavorited.value = !isFavorited.value;

      // Here you would typically save to a favorites service
      // For now, we'll just store in localStorage as a demo
      const favorites = JSON.parse(localStorage.getItem('wallpaper-favorites') || '[]');

      if (isFavorited.value) {
        if (!favorites.includes(wallpaper.value.id)) {
          favorites.push(wallpaper.value.id);
          notification.value = {
            show: true,
            type: 'success',
            message: 'Added to favorites!',
            resolution: ''
          };
        }
      } else {
        const index = favorites.indexOf(wallpaper.value.id);
        if (index > -1) {
          favorites.splice(index, 1);
          notification.value = {
            show: true,
            type: 'success',
            message: 'Removed from favorites',
            resolution: ''
          };
        }
      }

      localStorage.setItem('wallpaper-favorites', JSON.stringify(favorites));
      setTimeout(hideNotification, 2000);

    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      // Revert the state if there was an error
      isFavorited.value = !isFavorited.value;
      notification.value = {
        show: true,
        type: 'error',
        message: 'Failed to update favorites',
        resolution: ''
      };
      setTimeout(hideNotification, 3000);
    }
  };

  const checkIfFavorited = () => {
    if (!wallpaper.value) return;

    try {
      const favorites = JSON.parse(localStorage.getItem('wallpaper-favorites') || '[]');
      isFavorited.value = favorites.includes(wallpaper.value.id);
    } catch (error) {
      console.error('Failed to check favorites:', error);
      isFavorited.value = false;
    }
  };

  const showDownloadSuccess = (resolution: ResolutionOption) => {
    notification.value = {
      show: true,
      type: 'success',
      message: 'Download started!',
      resolution: resolution.dimensions
    };

    setTimeout(hideNotification, 5000);
  };

  const showDownloadError = (error: Error) => {
    notification.value = {
      show: true,
      type: 'error',
      message: error.message || 'Failed to download wallpaper',
      resolution: ''
    };

    setTimeout(hideNotification, 5000);
  };

  const hideNotification = () => {
    notification.value.show = false;
  };

  const goBack = () => {
    router.go(-1);
  };

  // Keyboard navigation support
  const handleKeydown = (event: KeyboardEvent) => {
    if (!wallpaper.value || !selectedDevice.value) return;
    const variations = getDeviceVariations(wallpaper.value, selectedDevice.value);
    if (variations.length <= 1) return;

    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
      return;
    }

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        navigateToPreviousVariation();
        break;
      case 'ArrowRight':
        event.preventDefault();
        navigateToNextVariation();
        break;
      case 'Home':
        event.preventDefault();
        handleVariationSelection(selectedDevice.value, 0);
        break;
      case 'End':
        event.preventDefault();
        handleVariationSelection(selectedDevice.value, variations.length - 1);
        break;
    }
  };

  // Initialize
  onMounted(() => {
    loadWallpaper();
    window.addEventListener('keydown', handleKeydown);

    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  });

  return {
    // State
    wallpaper,
    loading,
    error,
    isDownloading,
    showImageViewer,
    selectedDevice,
    notification,
    isFavorited,
    
    // Computed
    categoryName,
    availableDevices,
    deviceInfo,
    currentImageUrl,
    currentDimensions,
    currentVariationIndex,
    
    // Methods
    getDeviceDisplayName,
    getDeviceVariations,
    calculateAspectRatio,
    mapToDevicePreviewType,
    navigateToPreviousVariation,
    navigateToNextVariation,
    handleVariationSelection,
    handleDeviceSelection,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    getAvailableResolutions,
    downloadWallpaper,
    shareWallpaper,
    toggleFavorite,
    hideNotification,
    goBack
  };
}